import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Mail, Check, Flame } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic "Coming Soon" tile with image preview + email-capture waitlist.
 * Props:
 *  - slug:      unique slug stored in the backend (e.g. "we-have-heart-reissue")
 *  - title:     big T-shirt-style title
 *  - subtitle:  small uppercase eyebrow (e.g. "REISSUE · FIXED PRINT")
 *  - blurb:     body copy
 *  - image:     image src
 *  - featured:  bool — if true, render the wide 2-up hero layout, otherwise compact card
 */
export default function WaitlistTile({ slug, title, subtitle, blurb, image, featured = false }) {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get(`/waitlist/${slug}`)
      .then((r) => setTotal(r.data?.total_signups || 0))
      .catch(() => {});
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Drop a real email, friend.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/waitlist", { slug, email });
      setJoined(true);
      setTotal(r.data?.total_signups || total + 1);
      toast.success("You're on the list — we'll holler when it drops.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join the list");
    } finally {
      setSubmitting(false);
    }
  };

  const FormBlock = (
    <>
      {joined ? (
        <div data-testid={`waitlist-joined-${slug}`} className="mt-4 border-2 border-[#4CAF50] bg-[#4CAF50]/10 p-3 flex items-center gap-3">
          <Check className="w-5 h-5 text-[#4CAF50] shrink-0" />
          <div className="font-anton uppercase tracking-widest text-sm text-[#4CAF50]">You're on the list</div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid={`waitlist-form-${slug}`}>
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D9688]" />
            <input
              type="email"
              required
              data-testid={`waitlist-email-${slug}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full pl-9 pr-3 py-3 bg-[#0A0E0B] border-2 border-[#2C332A] text-[#E5E5E5] focus:border-[#DD5422] outline-none uppercase tracking-widest text-xs font-anton"
            />
          </div>
          <button
            type="submit"
            data-testid={`waitlist-submit-${slug}`}
            disabled={submitting}
            className="px-5 py-3 bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-sm hover:bg-[#FF6B35] disabled:opacity-50 border-2 border-[#DD5422]"
          >
            {submitting ? "..." : "Notify Me"}
          </button>
        </form>
      )}
      {total > 0 && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-[#8D9688]" data-testid={`waitlist-count-${slug}`}>
          🔥 {total} {total === 1 ? "redneck" : "rednecks"} on the list
        </div>
      )}
    </>
  );

  if (featured) {
    return (
      <div data-testid={`waitlist-tile-${slug}`} className="group bg-[#151A14] border-2 border-[#DD5422] fade-up md:col-span-2 lg:col-span-3 overflow-hidden">
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square md:aspect-auto bg-[#0A0E0B] overflow-hidden">
            <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute top-3 left-3 bg-[#DD5422] text-[#0A0E0B] px-3 py-1 font-anton text-xs uppercase tracking-widest flex items-center gap-1.5">
              <Flame className="w-3 h-3" fill="currentColor" /> Coming Soon
            </div>
          </div>
          <div className="p-6 md:p-10 flex flex-col justify-center">
            {subtitle && <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-2">/ {subtitle}</div>}
            <h3 className="font-anton text-3xl md:text-5xl uppercase tracking-tight leading-none">{title}</h3>
            <p className="text-[#8D9688] mt-3 max-w-md">{blurb}</p>
            {FormBlock}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`waitlist-tile-${slug}`} className="group bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors fade-up overflow-hidden flex flex-col">
      <div className="relative aspect-square bg-[#0A0E0B] overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 bg-[#DD5422] text-[#0A0E0B] px-2.5 py-1 font-anton text-[10px] uppercase tracking-widest flex items-center gap-1">
          <Flame className="w-3 h-3" fill="currentColor" /> Coming Soon
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        {subtitle && <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-[10px] mb-1.5">/ {subtitle}</div>}
        <h3 className="font-anton text-2xl uppercase tracking-tight leading-none">{title}</h3>
        {blurb && <p className="text-[#8D9688] mt-2 text-sm flex-1">{blurb}</p>}
        {FormBlock}
      </div>
    </div>
  );
}
